import {
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  HStack,
  VStack,
} from "@chakra-ui/react";
import DropFilesHere from "modules/messaging/_shared/drop-files-here";
import { usePanelSize } from "modules/providers/panel-size-provider";
import React, { memo, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { TASK_DRAFT_ID } from "modules/utils/Constants";
import { useFile } from "modules/providers/file-provider";
import ComposeMessageThreadSubject from "../_shared/compose-message-thread-subject";
import ComposeMessageThreadHeader from "../_shared/compose-message-thread-header";
import { MessageThreadType } from "gql/graphql";
import { ClockIcon, PersonCircleIcon } from "modules/icons";
import colors from "app/colors";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import TaskDueDateSelector from "./task-due-date-selector";
import QuillMessageComposer, {
  IQuillEditorHandle,
} from "../compose/quill-message-composer";
import ComposeTaskAssigneeSelector from "./compose-task-assignee-selector";

const ComposeTaskDrawer: React.FC = () => {
  const quillEditorRef = useRef<IQuillEditorHandle>(null);
  const { uploadFiles } = useFile();
  const { rightWidth } = usePanelSize();

  const { draft } = useMessageThreadDraft({
    draftId: TASK_DRAFT_ID,
  });

  const onFileListUpload = useCallback(
    (files: File[]) => uploadFiles(TASK_DRAFT_ID, files),
    [uploadFiles],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFileListUpload,
    noClick: true,
  });

  if (!draft) return null;

  return (
    <DrawerContent {...getRootProps()} w={rightWidth} maxW={rightWidth}>
      <DropFilesHere isDragActive={isDragActive} width={rightWidth} />
      <DrawerHeader px={3} py={0}>
        <ComposeMessageThreadHeader
          draftId={TASK_DRAFT_ID}
          title="New Task"
          type={MessageThreadType.Task}
          quillEditorRef={quillEditorRef}
        />
        <VStack pt={4} w="full" spacing={4} alignItems="flex-start">
          <ComposeMessageThreadSubject draftId={TASK_DRAFT_ID} autoFocus />
          <HStack w="full">
            <PersonCircleIcon fill={colors.gray30} boxSize={5} />
            <ComposeTaskAssigneeSelector draftId={TASK_DRAFT_ID} />
          </HStack>
          <HStack w="full">
            <ClockIcon fill={colors.gray30} boxSize={5} />
            <TaskDueDateSelector draftId={TASK_DRAFT_ID} />
          </HStack>
        </VStack>
      </DrawerHeader>

      <DrawerBody p={0} display="flex" flexDirection="column" overflow="hidden">
        <QuillMessageComposer
          draftId={TASK_DRAFT_ID}
          type={MessageThreadType.Task}
          expandOnInit
          ref={quillEditorRef}
        />
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(ComposeTaskDrawer);
